import { VStack, Text, Button, Icon, Box, HStack, useColorModeValue } from '@chakra-ui/react';
import { MdOpenInNew, MdWhatsapp } from 'react-icons/md';

interface WhatsAppUploadTaskProps {
  taskName: string;
  botCode: string;
  taskId: string;
  repId: number;
  mediaType: string; // 'photo_upload' | 'video_upload' | 'audio_upload' | 'document_upload' | 'media_upload'
}

const WHATSAPP_NUMBER = import.meta.env.VITE_APP_WHATSAPP_NUMBER;

const BASE62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/** Encode chatbot_code, task_id (xid), and rep_id into a base62 token.
 *  Must match the Go DecodeUploadRef in chatbot/actions/base62.go. */
function encodeUploadRef(botCode: string, taskId: string, repId: number): string {
  const plain = `${botCode}:${taskId}:${repId}`;
  const bytes = new TextEncoder().encode(plain);

  // Convert bytes to a big integer (big-endian)
  let num = 0n;
  for (const b of bytes) {
    num = (num << 8n) | BigInt(b);
  }

  if (num === 0n) return BASE62_CHARS[0];

  const base = 62n;
  let encoded = '';
  while (num > 0n) {
    const remainder = Number(num % base);
    encoded = BASE62_CHARS[remainder] + encoded;
    num = num / base;
  }

  // Preserve leading zero bytes
  for (const b of bytes) {
    if (b !== 0) break;
    encoded = BASE62_CHARS[0] + encoded;
  }

  return encoded;
}

function getMediaLabel(mediaType: string): string {
  switch (mediaType) {
    case 'photo_upload':
      return 'photo';
    case 'video_upload':
      return 'video';
    case 'audio_upload':
      return 'audio';
    case 'document_upload':
      return 'document';
    default:
      return 'file';
  }
}

export function WhatsAppUploadTask({
  taskName,
  botCode,
  taskId,
  repId,
  mediaType,
}: WhatsAppUploadTaskProps) {
  const mediaLabel = getMediaLabel(mediaType);
  const token = encodeUploadRef(botCode, taskId, repId);
  const command = `/upload ${token}`;
  const deepLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(command)}`;

  const bgColor = useColorModeValue('green.50', 'rgba(37, 211, 102, 0.1)');
  const borderColor = useColorModeValue('green.200', 'green.700');

  return (
    <VStack spacing={4} align="stretch">
      <Box
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        bg={bgColor}
        p={5}
      >
        <VStack spacing={3} align="center">
          <Icon as={MdWhatsapp} boxSize={10} color="green.500" />
          <Text fontSize="md" fontWeight="medium" color="text-primary" textAlign="center">
            Upload {mediaLabel} via WhatsApp
          </Text>
          <Text fontSize="sm" color="text-secondary" textAlign="center">
            Tap the button below to open WhatsApp. Send the pre-filled command,
            then send your {mediaLabel}.
          </Text>
          <Button
            as="a"
            href={deepLink}
            target="_blank"
            rel="noopener noreferrer"
            colorScheme="whatsapp"
            size="lg"
            leftIcon={<MdWhatsapp />}
            rightIcon={<MdOpenInNew size={14} />}
            w="full"
            maxW="300px"
          >
            Open WhatsApp
          </Button>
        </VStack>
      </Box>

      <Box px={2}>
        <Text fontSize="xs" fontWeight="medium" color="text-secondary" mb={1}>
          How it works:
        </Text>
        <VStack align="start" spacing={1}>
          <HStack spacing={2} align="start">
            <Text fontSize="xs" color="text-muted" minW="16px">1.</Text>
            <Text fontSize="xs" color="text-muted">Tap "Open WhatsApp" — it opens with a pre-filled command</Text>
          </HStack>
          <HStack spacing={2} align="start">
            <Text fontSize="xs" color="text-muted" minW="16px">2.</Text>
            <Text fontSize="xs" color="text-muted">Send the command message</Text>
          </HStack>
          <HStack spacing={2} align="start">
            <Text fontSize="xs" color="text-muted" minW="16px">3.</Text>
            <Text fontSize="xs" color="text-muted">Send your {mediaLabel} in the chat</Text>
          </HStack>
          <HStack spacing={2} align="start">
            <Text fontSize="xs" color="text-muted" minW="16px">4.</Text>
            <Text fontSize="xs" color="text-muted">Come back here and refresh to see the update</Text>
          </HStack>
        </VStack>
      </Box>
    </VStack>
  );
}
